import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSafeBack } from '../hooks/useSafeBack';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { getAuthHeader } from '../services/authService';
import { toast } from 'react-toastify';

interface PatientResultData {
  patientId: number;
  patientFirstName: string;
  patientLastName: string;
  patientSchoolId: string;
  patientEmail: string;
  patientPhone: string;
  assessmentId: number;
  assessmentName: string;
  assessmentCategory: string;
  completedAt: string | null;
  completed: boolean;
  totalScore: number;
  maxScore: number;
  flagLevel: 'RED' | 'YELLOW' | 'GREEN';
  riskAssessment: string;
  responses: QuestionResponse[];
  providerNotes: string;
  followUpHistory: FollowUpNote[];
  hasFollowUp: boolean;
  lastFollowUpDate: string | null;
  canReassign: boolean;
  canAddFollowUp: boolean;
  canExport: boolean;
}

interface QuestionResponse {
  questionId: number;
  questionText: string;
  questionType: string;
  answer: string;
  points: number;
  redFlag: boolean;
  hint: string;
}

interface FollowUpNote {
  noteId: number;
  note: string;
  createdBy: string;
  createdAt: string;
  followUpType: string;
}

const ProviderPatientResultPage: React.FC = () => {
  const { patientId, assessmentId } = useParams<{ patientId: string; assessmentId: string }>();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const goBack = useSafeBack();
  const [loading, setLoading] = useState(true);
  const [resultData, setResultData] = useState<PatientResultData | null>(null);
  const [followUpDialog, setFollowUpDialog] = useState(false);
  const [followUpNote, setFollowUpNote] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    fetchPatientResult();
  }, [patientId, assessmentId]);

  const fetchPatientResult = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/providers/patients/${patientId}/assessments/${assessmentId}/results`,
        { headers: getAuthHeader() }
      );
      setResultData(response.data);
    } catch (error) {
      console.error('Error fetching patient result:', error);
      toast.error('Failed to load patient assessment results');
    } finally {
      setLoading(false);
    }
  };


  const handleLogout = () => {
    logout();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleReassignSurvey = async () => {
    handleMenuClose();
    try {
      // TODO: Implement reassign survey API call
      toast.info('Reassign survey functionality coming soon');
    } catch (error) {
      console.error('Error reassigning survey:', error);
      toast.error('Failed to reassign survey');
    }
  };

  const handleAddFollowUp = () => {
    handleMenuClose();
    setFollowUpDialog(true);
  };

  const handleSubmitFollowUp = async () => {
    try {
      // TODO: Implement add follow-up note API call
      toast.success('Follow-up note added successfully');
      setFollowUpDialog(false);
      setFollowUpNote('');
      // Refresh data
      await fetchPatientResult();
    } catch (error) {
      console.error('Error adding follow-up:', error);
      toast.error('Failed to add follow-up note');
    }
  };

  const handleExportPDF = async () => {
    handleMenuClose();
    try {
      // TODO: Implement PDF export
      toast.info('PDF export functionality coming soon');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  const getRiskIcon = (flagLevel: string) => {
    switch (flagLevel) {
      case 'GREEN':
        return <CheckCircleIcon color="success" />;
      case 'YELLOW':
        return <WarningIcon color="warning" />;
      case 'RED':
        return <ErrorIcon color="error" />;
      default:
        return <CheckCircleIcon color="inherit" />;
    }
  };

  const getRiskColor = (flagLevel: string) => {
    switch (flagLevel) {
      case 'GREEN': return 'success';
      case 'YELLOW': return 'warning';
      case 'RED': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!resultData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Failed to load patient assessment results</Alert>
      </Container>
    );
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Psychology Profile System - Patient Assessment Result
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header with Back Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            Patient Assessment Result
          </Typography>
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        </Box>

        {/* Patient Information Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Patient Information
                </Typography>
                <Typography>
                  <strong>Name:</strong> {resultData.patientLastName}, {resultData.patientFirstName}
                </Typography>
                <Typography>
                  <strong>School ID:</strong> {resultData.patientSchoolId}
                </Typography>
                <Typography>
                  <strong>Email:</strong> {resultData.patientEmail}
                </Typography>
                <Typography>
                  <strong>Phone:</strong> {resultData.patientPhone}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Assessment Information
                </Typography>
                <Typography>
                  <strong>Assessment:</strong> {resultData.assessmentName}
                </Typography>
                <Typography>
                  <strong>Category:</strong> {resultData.assessmentCategory}
                </Typography>
                <Typography>
                  <strong>Completed:</strong> {resultData.completedAt ? 
                    new Date(resultData.completedAt).toLocaleString() : 
                    'Not completed'}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    icon={getRiskIcon(resultData.flagLevel)}
                    label={`${resultData.flagLevel} FLAG - ${resultData.riskAssessment}`}
                    color={getRiskColor(resultData.flagLevel) as any}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Score Summary */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Assessment Score
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h3">
                {resultData.totalScore} / {resultData.maxScore}
              </Typography>
              <Alert severity={
                resultData.flagLevel === 'RED' ? 'error' : 
                resultData.flagLevel === 'YELLOW' ? 'warning' : 
                'success'
              }>
                Patient is in {resultData.flagLevel} FLAG area - {resultData.riskAssessment}
              </Alert>
            </Box>
          </CardContent>
        </Card>

        {/* Questions and Responses */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Questions and Responses
            </Typography>
            <List>
              {resultData.responses.map((response, index) => (
                <Paper key={response.questionId} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {index + 1}. {response.questionText}
                  </Typography>
                  <Typography sx={{ mb: 1 }}>
                    <strong>Answer:</strong> {response.answer}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2">
                      <strong>Points:</strong> {response.points}
                    </Typography>
                    {response.redFlag && (
                      <Chip
                        size="small"
                        label="Red Flag"
                        color="error"
                      />
                    )}
                  </Box>
                  {response.hint && (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      <strong>Hint:</strong> {response.hint}
                    </Typography>
                  )}
                </Paper>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Provider Notes */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Provider Notes
            </Typography>
            {resultData.providerNotes ? (
              <Typography>{resultData.providerNotes}</Typography>
            ) : (
              <Typography color="textSecondary">No provider notes available</Typography>
            )}
          </CardContent>
        </Card>

        {/* Follow-up History */}
        {resultData.followUpHistory.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Follow-up History
              </Typography>
              <List>
                {resultData.followUpHistory.map((note) => (
                  <ListItem key={note.noteId} divider>
                    <ListItemText
                      primary={note.note}
                      secondary={`${note.createdBy} - ${new Date(note.createdAt).toLocaleString()} (${note.followUpType})`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {resultData.canReassign && (
            <MenuItem onClick={handleReassignSurvey}>
              <ListItemIcon>
                <AssignmentReturnIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Reassign Survey</ListItemText>
            </MenuItem>
          )}
          {resultData.canAddFollowUp && (
            <MenuItem onClick={handleAddFollowUp}>
              <ListItemIcon>
                <NoteAddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add Follow-up Note</ListItemText>
            </MenuItem>
          )}
          {resultData.canExport && (
            <MenuItem onClick={handleExportPDF}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export as PDF</ListItemText>
            </MenuItem>
          )}
        </Menu>

        {/* Follow-up Dialog */}
        <Dialog
          open={followUpDialog}
          onClose={() => setFollowUpDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Follow-up Note</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Follow-up Note"
              fullWidth
              multiline
              rows={4}
              value={followUpNote}
              onChange={(e) => setFollowUpNote(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFollowUpDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitFollowUp} variant="contained">
              Add Note
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default ProviderPatientResultPage;